<!--
  O catálogo GLOBAL de combos (NS 2026-0090). Serviço e prioridade definem o que
  o combo cobre; preço e ativação por unidade ficam em Preço dos combos.
-->
<template>
  <data-table
    ref="datatableRef"
    :config="datatableConfig"
    :endpoint="endpoint"
    :autoLoad="true"
    :refreshButton="{ show: true, text: t('Atualizar') }"
    :csvButton="{ show: true, text: t('CSV') }"
    @data-error="handleDataError"
    @button-click="handleButtonClick"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import DataTable from '../../grid/DataTable.vue';
import type { Combos_ComboRow } from '../../contracts/Combos_ComboRow';
import { comboExplanations } from './explanations';

const { t } = useI18n();

const emit = defineEmits<{
  edit: [id: number];
}>();

const datatableRef = ref<{ loadData: () => void } | null>(null);
const endpoint = '/index.php/so/combos/lista';
const EX = computed(() => comboExplanations(t));

const comoLinha = (row: Record<string, unknown>): Combos_ComboRow => row as unknown as Combos_ComboRow;

const datatableConfig = computed(() => ({
  tableId: 'so-combos',
  rowTestId: (data: Record<string, unknown>) => `so-combos-row-${comoLinha(data).so_combo_id}`,
  filename: `so_combos_${new Date().toISOString().split('T')[0]}`,
  columns: [
    { data: 'so_combo_id', title: t('ID'), width: 60, sortable: true },
    { data: 'so_combo_name', title: t('Nome'), sortable: true, filterType: 'text' },
    { data: 'so_service_name', title: t('Serviço'), sortable: true, filterType: 'text' },
    { data: 'so_priority_name', title: t('Prioridade'), width: 150, sortable: true, filterType: 'text' },
    {
      data: 'so_combo_quantity',
      title: t('Pares'),
      width: 90,
      sortable: true,
      renderType: 'integer',
      explanation: EX.value.pares,
    },
    {
      data: 'so_combo_validity_days',
      title: t('Validade (dias)'),
      width: 130,
      sortable: true,
      renderType: 'integer',
      explanation: EX.value.validade,
    },
    {
      data: 'so_combo_active',
      title: t('Situação'),
      width: 120,
      sortable: true,
      renderType: 'badge',
      explanation: EX.value.catalogoSituacao,
      badgeConfig: (row: Record<string, unknown>) => {
        const ativo = Number(comoLinha(row).so_combo_active) === 1;
        return { label: ativo ? t('Ativo') : t('Desligado'), severity: ativo ? 'success' : 'secondary' };
      },
    },
    {
      data: null,
      title: t('Editar'),
      width: 110,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Editar'),
      buttonIcon: 'pi pi-pencil',
      buttonAction: 'editar',
      buttonTestId: (row: Record<string, unknown>) => `so-combos-edit-${comoLinha(row).so_combo_id}`,
    },
  ],
  pageLength: 25,
  pagination: true,
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const loadData = (): void => {
  datatableRef.value?.loadData();
};

const handleDataError = (error: { message?: string } | Error): void => {
  eventBus.emit('globalMessage.error', t('Erro ao carregar os combos') + ': ' + (error.message || ''));
};

const handleButtonClick = (evento: { data: Record<string, unknown> | null; action?: unknown }): void => {
  if (evento.action === 'editar' && evento.data) {
    emit('edit', comoLinha(evento.data).so_combo_id);
  }
};

defineExpose({ loadData });
</script>
